import type { KeyboardEvent, ReactNode } from 'react';
import './Abas.css';

export type Aba = {
  readonly id: string;
  readonly rotulo: string;
  /** Contagem ao lado do rótulo — quantos registros a aba tem. */
  readonly contador?: number;
};

type Props = {
  /** Nome da lista de abas para o leitor de tela ("Usuários"). */
  readonly rotulo: string;
  readonly abas: readonly Aba[];
  readonly ativa: string;
  readonly aoTrocar: (id: string) => void;
  /** Prefixo dos ids, quando houver mais de um grupo de abas na mesma tela. */
  readonly grupo?: string;
};

function idDaAba(grupo: string, id: string): string {
  return `${grupo}-aba-${id}`;
}

function idDoPainel(grupo: string, id: string): string {
  return `${grupo}-painel-${id}`;
}

/**
 * Abas do painel.
 *
 * Segue o padrão de abas do ARIA: a aba corrente é a única na ordem de
 * tabulação, e as setas andam entre elas dando a volta nas pontas. Sem isso,
 * quem navega por teclado atravessa uma aba por Tab — e são duas telas
 * inteiras dentro de cada uma (UC13).
 */
export function Abas({ rotulo, abas, ativa, aoTrocar, grupo = 'abas' }: Props) {
  function aoTeclar(evento: KeyboardEvent<HTMLDivElement>) {
    const passo = evento.key === 'ArrowRight' ? 1 : evento.key === 'ArrowLeft' ? -1 : 0;

    if (passo === 0 || abas.length === 0) {
      return;
    }

    evento.preventDefault();
    const atual = abas.findIndex((aba) => aba.id === ativa);
    const proxima = abas[(atual + passo + abas.length) % abas.length];

    if (proxima) {
      aoTrocar(proxima.id);
    }
  }

  return (
    <div className="abas" role="tablist" aria-label={rotulo} onKeyDown={aoTeclar}>
      {abas.map((aba) => {
        const selecionada = aba.id === ativa;

        return (
          <button
            key={aba.id}
            id={idDaAba(grupo, aba.id)}
            type="button"
            role="tab"
            className={selecionada ? 'abas__aba abas__aba--ativa' : 'abas__aba'}
            aria-selected={selecionada}
            aria-controls={idDoPainel(grupo, aba.id)}
            tabIndex={selecionada ? 0 : -1}
            onClick={() => {
              aoTrocar(aba.id);
            }}
          >
            {aba.rotulo}
            {aba.contador !== undefined && <span className="abas__contador">{aba.contador}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Conteúdo de uma aba, nomeado pela aba que o abriu. */
export function PainelDeAba({
  id,
  grupo = 'abas',
  children,
}: {
  readonly id: string;
  readonly grupo?: string;
  readonly children: ReactNode;
}) {
  return (
    <div id={idDoPainel(grupo, id)} role="tabpanel" aria-labelledby={idDaAba(grupo, id)}>
      {children}
    </div>
  );
}
